"use client";

import { useState, useEffect } from "react";
import { hasCookie, setCookie, getCookie } from "cookies-next";

// Types for consent management
interface ConsentState {
  functional: boolean;
  analytics: boolean;
}

interface CookieConsent {
  hasConsented: boolean;
  consent: ConsentState;
  timestamp: string;
}

const COOKIE_NAME = "cookie_consent";
const COOKIE_DURATION = 60 * 60 * 24 * 180; // 6 months in seconds

const DEFAULT_CONSENT: ConsentState = {
  functional: true, // Always enabled (required)
  analytics: false,
};

export default function CookieConsentBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomization, setShowCustomization] = useState(false);
  const [consentState, setConsentState] =
    useState<ConsentState>(DEFAULT_CONSENT);

  useEffect(() => {
    // Check for existing consent cookie
    const hasConsent = hasCookie(COOKIE_NAME);

    setShowBanner(!hasConsent);

    // If consent already exists, load preferences
    if (hasConsent) {
      const savedConsent = getCookie(COOKIE_NAME);

      try {
        const parsed: CookieConsent = JSON.parse(savedConsent as string);

        setConsentState(parsed.consent);
        // Trigger authorized scripts loading
        triggerScripts(parsed.consent);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error reading consent:", error);
      }
    }
  }, []);

  const saveConsent = (consent: ConsentState) => {
    const consentData: CookieConsent = {
      hasConsented: true,
      consent,
      timestamp: new Date().toISOString(),
    };

    setCookie(COOKIE_NAME, JSON.stringify(consentData), {
      maxAge: COOKIE_DURATION,
      sameSite: "lax",
      secure: process.env.NODE_ENV === "production",
    });

    setConsentState(consent);
    setShowBanner(false);
    setShowCustomization(false);

    // Trigger authorized scripts loading
    triggerScripts(consent);
  };

  const triggerScripts = (consent: ConsentState) => {
    // Trigger custom event to notify scripts
    window.dispatchEvent(
      new CustomEvent("cookieConsentUpdated", {
        detail: consent,
      }),
    );

    // Log for debug
    // eslint-disable-next-line no-console
    console.log("🍪 Consent updated:", consent);
  };

  const handleAcceptAll = () => {
    saveConsent({
      functional: true,
      analytics: true,
    });
  };

  const handleRefuseAll = () => {
    saveConsent({
      functional: true, // Always required
      analytics: false,
    });
  };

  const handleCustomize = () => {
    setShowCustomization(true);
  };

  const handleSaveCustom = () => {
    saveConsent(consentState);
  };

  const toggleConsent = (type: keyof ConsentState) => {
    if (type === "functional") return; // Functional cookies are required

    setConsentState((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Function to modify preferences (called from footer)
  const openPreferences = () => {
    setShowBanner(true);
    setShowCustomization(true);
  };

  // Expose function globally for footer link
  useEffect(() => {
    (window as any).openCookiePreferences = openPreferences;

    return () => {
      delete (window as any).openCookiePreferences;
    };
  }, []);

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* Overlay for customization modal */}
      {showCustomization && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                Customize my cookie preferences
              </h2>

              <div className="space-y-4">
                {/* Functional cookies */}
                <div className="border-b border-gray-200 dark:border-gray-600 pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        Functional cookies
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Required for site functionality (navigation, security)
                      </p>
                    </div>
                    <div className="bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-200 px-3 py-1 rounded text-sm">
                      Required
                    </div>
                  </div>
                </div>

                {/* Analytics cookies */}
                <div className="pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        Analytics
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        Google Analytics to understand site usage
                      </p>
                    </div>
                    <button
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        consentState.analytics
                          ? "bg-indigo-600"
                          : "bg-gray-200 dark:bg-gray-600"
                      }`}
                      onClick={() => toggleConsent("analytics")}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          consentState.analytics
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  onClick={handleSaveCustom}
                >
                  Save my preferences
                </button>
                <button
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white text-sm transition-colors"
                  onClick={() => setShowCustomization(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main banner */}
      {!showCustomization && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-600 shadow-lg">
          <div className="max-w-7xl mx-auto p-4">
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 dark:text-white mb-2">
                  Privacy Notice
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  We use cookies to ensure proper site functionality and measure
                  audience. You can accept all cookies or customize your
                  preferences.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 lg:ml-4">
                <button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  onClick={handleAcceptAll}
                >
                  Accept all
                </button>
                <button
                  className="border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 px-4 py-2 rounded-lg text-sm transition-colors"
                  onClick={handleRefuseAll}
                >
                  Refuse all
                </button>
                <button
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 px-4 py-2 text-sm font-medium transition-colors"
                  onClick={handleCustomize}
                >
                  Customize
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
